import { debounce } from "lodash-es";

/**
 * 判断类型工厂
 */
export const TypeClass = (() => {
  const checkTypeArr = [
    "Function",
    "Promise",
    "Array",
    "Object",
    "String",
    "Number",
    "Undefined",
    "Null",
    "Boolean"
  ];
  const res = Object.create(null);
  for (let type of checkTypeArr) {
    res[`is${type}`] = (target: any) =>
      Object.prototype.toString.call(target).slice(8, -1) === type;
  }
  return res;
})();

/**
 * [object Object]  ===>  "Object"
 * @param target
 */
export const checkType = (target: any): string => {
  return Object.prototype.toString.call(target).slice(8, -1);
};

/**
 * @param target 是否是函数
 */
export const isFunction = (target: any): boolean => {
  return checkType(target) === "Function";
};

/**
 * @param target 是否是Promise
 */
export const isPromise = (target: any): boolean => {
  return checkType(target) === "Promise";
};
/**
 * @param target 是否是String
 */
export const isString = (target: any): boolean => {
  return checkType(target) === "String";
};
/**
 * @param target 是否是Number
 */
export const isNumber = (target: any): boolean => {
  return checkType(target) === "Number";
};

// isObject
export const isObject = target => {
  return Object.prototype.toString.call(target) === "[object Object]";
};

/**
 * 是否是 null  undefined
 * @param val
 */
export const isEmptyValue = (val: any) => {
  return TypeClass.isNull(val) || TypeClass.isUndefined(val);
};

/**
 * 防抖，默认是立即执行
 * @param fn 执行函数
 * @param time 控制时间
 * @param options 自定义参数
 */
export const debounceImmediate = (
  fn: Function,
  time?: number,
  options?: any
) => {
  return debounce(
    fn,
    time || 2000,
    options || {
      leading: true,
      trailing: false
    }
  );
};

/**
 * 数组自定义排序
 * @param list
 * @param field 数组里面 item 中需要排序的属性名
 * @param format 格式化属性值后，排序
 */
export const sortList = <T>(
  list: T[],
  field: string,
  format: Function = (val: any) => val
) => {
  return list.sort((prev, next) => format(prev[field]) - format(next[field]));
};

/**
 * 获取Promise数组中的 值
 * @param data Promise 数组
 */
export const getValueFromPromiseArray = async (data: Promise<any>[]) => {
  return (await Promise.allSettled(data)).map((o: any) => o.value);
};

/**
 * 延迟
 * @param time 时长
 */
export const customSleep = function (time = 1000) {
  return new Promise(resolve => {
    const timer = setTimeout(() => {
      timer && clearTimeout(timer);
      resolve("");
    }, time);
  });
};
/**
 * 获取随机数
 */
export function getUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/*
 * 计算字符串长度(英文占1个字符，中文汉字占2个字符)
 */
const calcCharLength = (charCode, realLength) => {
  if (charCode >= 0 && charCode <= 128) {
    return (realLength += 1);
  } else {
    return (realLength += 2);
  }
};
/*
 * 计算字符串长度(英文占1个字符，中文汉字占2个字符)
 */
export const getByteLen = function (str = "") {
  ///<summary>获得字符串实际长度，中文2，英文1</summary>
  ///<param name="str">要获得长度的字符串</param>
  var realLength = 0,
    len = str.length;
  for (var i = 0; i < len; i++) {
    realLength = calcCharLength(str.charCodeAt(i), realLength);
  }
  return realLength;
};
/*
 * 计算字符串长度(英文占1个字符，中文汉字占2个字符)
 * 截取 n 位
 */
export const splitChar = function (str = "", n = str.length) {
  var realLength = 0,
    len = str.length;
  for (var i = 0; i < len; i++) {
    if (realLength >= n) return str.slice(0, i);
    realLength = calcCharLength(str.charCodeAt(i), realLength);
  }
  return str;
};

/**
 * 过滤 null 和 undefined 和 空串
 * @param obj
 * @param reference 是否过滤引用类型。过滤 []、{}
 */
export const clearEmptyAttr = (obj = {}, reference = false) => {
  return Object.assign(
    {},
    ...Object.keys(obj)
      // filter process
      .filter(key => {
        let value = obj[key];
        const hasVal = !["", null, undefined].includes(value);
        // filter reference
        if (reference && typeof value === "object" && value) {
          if (Array.isArray(value)) {
            if (value.length) {
              obj[key] = value.map(o =>
                typeof o === "object" ? clearEmptyAttr(o, reference) : o
              );
            }
            return !!value.length;
          } else {
            clearEmptyAttr(value, reference);
            return !!Object.keys(value).length;
          }
        }
        return hasVal;
      })
      // final key
      .map(key => ({ [key]: obj[key] }))
  );
};

/**
 * 返回指定字段的对象
 * @param obj
 * @param fields 需要的字段数组
 */
export const filterFields = (obj = {}, fields = []) => {
  return Object.assign(
    {},
    ...Object.keys(obj)
      .filter(key => fields.includes(key))
      .map(key => ({ [key]: obj[key] }))
  );
};
